
import React from 'react'
import './App.css'
import {useNavigate, Outlet} from "react-router";
import Toolbar from "@mui/material/Toolbar";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from '@mui/material/AppBar';
import Menu from '@mui/material/Menu';
import MenuList from '@mui/material/MenuList';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {ThemeProvider} from '@mui/material/styles';
import useAuth from '../hooks/useAuth'
import {useGetMeQuery} from "../apis/CurrentUser.api";
import {DeleteDialog, useLocale, useLocaleTheme} from '../share';
import { DynamicSnackbar } from '../share';
import {Session} from "./slices/sessionSlice";
import ScopeSwitcher from "../share/components/scope/ScopeSwitcher.tsx";
import activateLocale from "../share/components/localization/Locale/locales.ts";
import {I18nProvider} from "@lingui/react";
import CookieConsent from '../share/components/gdpr/CookieConsent';
import MdiIcon from "../share/components/mdi/MdiIcon.tsx";
import {emailAvatar} from "../utils/color";
import {
    mdiTree,
} from "@mdi/js";

import {secondaryList as authSecondaryList} from '../auth/pages/listItems';
import {secondaryList as locationSecondaryList} from '../location/pages/listItems';
import {secondaryList as addressSecondaryList} from '../address/pages/listItems';
import {secondaryList as timberSecondaryList} from '../timber/pages/listItems';

import { t } from "@lingui/core/macro";

const i18n = activateLocale()

const StringAvatar = () => {

  const {data: user} = useGetMeQuery();

  return (
    <Avatar {...emailAvatar(user?.email)}/>
  )
}

const pages = (auth: boolean, session: Session, flatten = true) => {
    const defaultList = [
        {name: 'Home', label: t`Home`, path: '/'},
        {name: 'Dashboard', label: t`Dashboard`, path: '/dashboard'},
    ]

    if (auth) {
        const authList = session?.hasPermission('', 'applications/auth/entities/admin/list/view') ? [
            {type: 'divider'},
            {name: 'Admin', label: t`Admin`, path: '/admin'},
            {name: 'Swagger UI', label: t`Swagger UI`, path: '/swagger-ui'},
            {type: 'divider'},
        ] : []
        
        let timberPackageList = timberSecondaryList(session)
        if (timberPackageList.length) {
            timberPackageList.push({type: 'divider'})
        }
        let addressPackageList = addressSecondaryList(session)
        if (addressPackageList.length) {
            addressPackageList.push({type: 'divider'})
        }
        let locationPackageList = locationSecondaryList(session)
        if (locationPackageList.length) {
            locationPackageList.push({type: 'divider'})
        }
        let authPackageList = authSecondaryList(session)
        if (authPackageList.length) {
            authPackageList.push({type: 'divider'})
        }
        
        
        const packagesList = flatten ? [] :
            [
                ...timberPackageList,
                ...addressPackageList,
                ...locationPackageList,
                ...authPackageList,
                
            ]
        
        return [
            ...defaultList,
            ...authList,
            ...packagesList
        ]
    } else {
        return [
            {name: 'Home', label: t`Home`, path: '/'},
            {type: 'divider'},
            {name: 'Login', label: t`Login`, path: '/login'},
            
        ]
    }
};

const settings = (auth: boolean) => {
    if (auth) {
        return [
            {name: 'Profile', label: t`Profile`, path: '/profile'},
            {name: 'Preferences', label: t`Preferences`, path: '/preferences'},
            {name: 'ChangePassword', label: t`Change Password`, path: '/change-password'},
            {type: 'divider'},
            {name: 'Logout', label: t`Logout`, exec: (onLogoutHandler: any) => {onLogoutHandler()}},
        ]
    } else {
        return [
            {name: 'Preferences', label: t`Preferences`, path: '/preferences'},
            {name: 'Login', label: t`Login`, path: '/login'},
            
            {type: 'divider'},
            {name: 'Privacy', label: t`Privacy Policy`, path: '/privacy'},
            {name: 'Terms', label: t`Terms`, path: '/terms'},
        ]
    }
};


const App = () => {

    // TODO useAuthCheck()

    const { localeKey, dateFnsLocale } = useLocale()
    const { themeWithLocale } = useLocaleTheme(localeKey)

    const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null)
    const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null)

    const { isAuthenticated, handleLogout, session } = useAuth()

    const onLogoutHandler = async () => {
        await handleLogout();
    };

    const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElNav(event.currentTarget);
    };
    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const navigate = useNavigate()
    /* CRA: app hooks */

    // @ts-ignore
    if (i18n && localeKey && themeWithLocale)
        // @ts-ignore
        return (
            <ThemeProvider theme={themeWithLocale}>
                <CssBaseline/>
                <I18nProvider i18n={i18n} key={localeKey}>
                    <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={ dateFnsLocale }
                    >
                        <Box sx={ {display: 'flex'} }>
                        <DynamicSnackbar />
                        <AppBar position="static">
                            <Container maxWidth="xl">
                                <Toolbar disableGutters>

                                    <Box
                                        sx={ {
                                            mr: 2,
                                            display: {xs: 'none', md: 'flex'},
                                        } }
                                    >
                                        <MdiIcon d={ mdiTree } sx={ {display: {xs: 'none', md: 'flex'}, mr: 1} }
                                            htmlColor="lightgreen" fontSize="large"/>
                                        <Badge color="primary" badgeContent="prod" showZero={false}>
                                            <Typography
                                                variant="h6"
                                                noWrap
                                                component="a"
                                                href="#app-bar-with-responsive-menu"
                                                sx={ {
                                                    fontFamily: 'monospace',
                                                    fontWeight: 700,
                                                    letterSpacing: '.3rem',
                                                    color: 'inherit',
                                                    textDecoration: 'none',
                                                } }
                                            >
                                                TimberStorage
                                            </Typography>
                                        </Badge>
                                    </Box>

                                    <Box sx={ {flexGrow: 1, display: {xs: 'flex', md: 'none'} } }>
                                        <IconButton
                                            size="large"
                                            aria-controls="menu-appbar"
                                            aria-haspopup="true"
                                            onClick={handleOpenNavMenu}
                                            color="inherit"
                                        >
                                            <MenuIcon/>
                                        </IconButton>
                                        <Menu
                                            id="menu-appbar"
                                            anchorEl={anchorElNav}
                                            anchorOrigin={ {
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            } }
                                            keepMounted
                                            transformOrigin={ {
                                                vertical: 'top',
                                                horizontal: 'left',
                                            } }
                                            open={Boolean(anchorElNav)}
                                            onClose={handleCloseNavMenu}
                                            sx={ {
                                                display: {xs: 'block', md: 'none'},
                                            } }
                                        >
                                            {pages(isAuthenticated, session, false).map((page, index) => {
                                                if (page.type === 'divider')
                                                    return (
                                                        <Divider key={index}/>
                                                    )
                                                else
                                                    return (
                                                        <MenuItem key={page.name} onClick={() => navigate(page.path)}>
                                                            <Typography textAlign="center">{page.label || page.name}</Typography>
                                                        </MenuItem>
                                                    )
                                            }
                                           )}
                                        </Menu>
                                    </Box>

                                    <Box
                                        sx={ {
                                            mr: 2,
                                            display: {xs: 'flex', md: 'none'},
                                            flexGrow: 1,
                                        } }
                                    >
                                        <MdiIcon d={ mdiTree } sx={ {display: {xs: 'flex', md: 'none'}, mr: 1 } }
                                                 htmlColor="lightgreen" fontSize="large"/>
                                        <Badge color="primary" badgeContent="prod" showZero={false}>
                                            <Typography
                                                variant="h5"
                                                noWrap
                                                component="a"
                                                href="#app-bar-with-responsive-menu"
                                                sx={ {
                                                    fontFamily: 'monospace',
                                                    fontWeight: 700,
                                                    letterSpacing: '.3rem',
                                                    color: 'inherit',
                                                    textDecoration: 'none',
                                                } }
                                            >
                                                TimberStorage
                                            </Typography>
                                        </Badge>
                                    </Box>
                                    <Box sx={ {flexGrow: 1, display: {xs: 'none', md: 'flex'} } }>
                                        {pages(isAuthenticated, session, true).map((page, index) => {
                                            if (page.type === 'divider')
                                              return (
                                                <Divider key={index} orientation="vertical" flexItem variant="middle" sx={ {color: 'white'} }/>
                                              )
                                            else
                                              return (
                                                <Button
                                                    key={page.name}
                                                    onClick={() => navigate(page.path)}
                                                    sx={ {my: 2, color: 'white', display: 'block'} }
                                                >
                                                    {page.label || page.name}
                                                </Button>
                                              )
                                            }
                                        )}
                                    </Box>
                                    <Box sx={ {flexGrow: 0} }>
                                        <Tooltip title={t`Open settings`}>
                                            <IconButton onClick={handleOpenUserMenu} sx={ {p: 0} }>
                                                { isAuthenticated ?
                                                    <StringAvatar/>
                                                    :
                                                    <Avatar/>
                                                }
                                            </IconButton>
                                        </Tooltip>
                                        <Menu
                                            sx={ {mt: '45px'} }
                                            id="menu-appbar"
                                            anchorEl={anchorElUser}
                                            anchorOrigin={ {
                                                vertical: 'top',
                                                horizontal: 'right',
                                            } }
                                            keepMounted
                                            transformOrigin={ {
                                                vertical: 'top',
                                                horizontal: 'right',
                                            } }
                                            open={Boolean(anchorElUser)}
                                            onClose={handleCloseUserMenu}
                                        >
                                            {settings(isAuthenticated).map((setting, index) => {
                                                if (setting.type === 'divider')
                                                  return (
                                                    <Divider key={index}/>
                                                  )
                                                else
                                                  return (
                                                    <MenuItem key={setting.name} onClick={() => setting.path ? navigate(setting.path) : setting.exec(onLogoutHandler)}>
                                                        <Typography textAlign="center">{setting.label || setting.name}</Typography>
                                                    </MenuItem>
                                                  )
                                            })}
                                            { isAuthenticated &&
                                                <MenuList>
                                                    <Divider />
                                                    <ScopeSwitcher/>
                                                </MenuList>
                                            }
                                        </Menu>
                                    </Box>
                                </Toolbar>
                            </Container>
                            <Box>
                                <Outlet/>
                                <CookieConsent />
                            </Box>
                        </AppBar>
                    </Box>
                        <DeleteDialog/>
                    </LocalizationProvider>
                </I18nProvider>
            </ThemeProvider>
        )
    else
        return (
            <div>Loading...</div>
        )
}

export default App
